import {useI18n} from 'i18n/I18nContext'
import React, {useEffect, useRef, useCallback} from 'react'

interface SignaturePadProps {
  onSave: (dataUrl: string | null) => void
  signature?: string | null
  height?: number
  lineWidth?: number
  strokeColor?: string
  maxWidth?: number
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSave,
  signature,
  height = 320,
  lineWidth = 1.5,
  strokeColor = '#000',
  maxWidth = 600,
}) => {
  const {t} = useI18n()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
  const isDrawing = useRef(false)
  const lastPoint = useRef<{x: number; y: number} | null>(null)
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null)

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const wrapper = wrapperRef.current
    if (!canvas || !wrapper) return

    const width = wrapper.clientWidth
    const dpr = window.devicePixelRatio || 1

    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.scale(dpr, dpr)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = lineWidth
    ctx.strokeStyle = strokeColor
    ctxRef.current = ctx

    if (signature) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0, width, height)
      img.src = signature
    }
  }, [height, lineWidth, strokeColor, signature])

  useEffect(() => {
    resizeCanvas()

    const observer = new ResizeObserver(() => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current)
      resizeTimeout.current = setTimeout(() => resizeCanvas(), 50)
    })

    if (wrapperRef.current) observer.observe(wrapperRef.current)

    return () => {
      observer.disconnect()
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current)
    }
  }, [resizeCanvas])

  const getPoint = (x: number, y: number) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return {x: x - rect.left, y: y - rect.top}
  }

  const startDrawing = (x: number, y: number) => {
    isDrawing.current = true
    lastPoint.current = getPoint(x, y)
  }

  const draw = (x: number, y: number) => {
    if (!isDrawing.current || !ctxRef.current) return
    const newPoint = getPoint(x, y)
    const last = lastPoint.current
    if (last) {
      const ctx = ctxRef.current
      ctx.beginPath()
      ctx.moveTo(last.x, last.y)
      ctx.lineTo(newPoint.x, newPoint.y)
      ctx.stroke()
    }
    lastPoint.current = newPoint
  }

  const stopDrawing = () => {
    isDrawing.current = false
    lastPoint.current = null
    if (canvasRef.current) onSave(canvasRef.current.toDataURL('image/png'))
  }

  const handleMouseDown = (e: React.MouseEvent) =>
    startDrawing(e.clientX, e.clientY)
  const handleMouseMove = (e: React.MouseEvent) => draw(e.clientX, e.clientY)
  const handleMouseUp = () => stopDrawing()

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    const touch = e.touches[0]
    if (touch) startDrawing(touch.clientX, touch.clientY)
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    const touch = e.touches[0]
    if (touch) draw(touch.clientX, touch.clientY)
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault()
    stopDrawing()
  }

  const clearCanvas = useCallback(() => {
    const ctx = ctxRef.current
    const canvas = canvasRef.current
    if (!ctx || !canvas) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    onSave(null)
  }, [onSave])

  return (
    <div
      ref={wrapperRef}
      style={{
        width: '100%',
        maxWidth,
        margin: '8px auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height,
          border: '1px solid #ccc',
          borderRadius: 6,
          backgroundColor: '#fff',
          display: 'block',
          touchAction: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 8,
          justifyContent: 'center',
        }}
      >
        <button
          type="button"
          onClick={clearCanvas}
          style={{
            padding: '6px 12px',
            borderRadius: 4,
            border: '1px solid #999',
            background: '#f5f5f5',
            cursor: 'pointer',
          }}
        >
          {t('orgCert.clearSignature')}
        </button>
      </div>
    </div>
  )
}
